import {
  html,
  attr,
  Signal,
  ForEach,
  When,
  Fragment,
  NotEmpty,
  Ensure,
} from '@tempots/dom'
import { Anchor } from '@tempots/ui'
import { Icon } from '@tempots/beatui'
import { PageLayout } from '@/components/layout'
import { StatCard } from '@/components/ui'
import {
  apiKeys as apiKeysService,
  createProjectJobs,
  createProjectStorage,
} from '@/services/gateway'
import { Workspace } from '@/state/workspace'
import type { JobResponse } from '@/services/types'
import { formatRelativeTime, formatCredits } from '@/utils'
import { formatBytesSplit } from '@/utils/formatting'

export interface DashboardPageOptions {
  projectId: Signal<string>
}

// interface DashboardStats {
//   credits: number
//   storageUsedGB: number
//   storageDaysRemaining: number | null
//   activeApiKeys: number
// }

interface ActivityItem {
  id: string
  type: 'job' | 'upload' | 'purchase'
  title: string
  subtitle: string
  creditChange?: bigint
  timestamp: string
  iconColor: 'purple' | 'blue' | 'green' | 'red' | 'gray'
}

export function DashboardPage({ projectId }: DashboardPageOptions) {
  projectId.on(Workspace.changeProject)

  // const activity = prop<ActivityItem[]>([])

  // // Load dashboard data
  // const loadDashboard = async () => {
  //   // Create project-scoped jobs service
  //   const jobsService = createProjectJobs(pid)

  //   // Load data in parallel - all calls scoped to URL projectId
  //   const [orgResult, storageResult, apiKeysResult, jobsResult] =
  //     await Promise.allSettled([
  //       orgsService.getOrganization(orgId),
  //       storageService.getStatus(),
  //       apiKeysService.list(pid),
  //       jobsService.list({ pageSize: 5 }),
  //     ])

  //   // Extract credits from org data (creditBalance is a direct property)
  //   const credits =
  //     orgResult.status === 'fulfilled' &&
  //       orgResult.value?.creditBalance !== undefined
  //       ? orgResult.value.creditBalance
  //       : 0

  //   // Extract storage info (with null safety)
  //   // Note: API returns snake_case field names
  //   let storageUsedGB = 0
  //   let storageDaysRemaining: number | null = null
  //   if (storageResult.status === 'fulfilled' && storageResult.value) {
  //     storageUsedGB = storageResult.value.storage_used_gb ?? 0
  //     storageDaysRemaining =
  //       storageResult.value.projected_days_remaining ?? null
  //   }

  //   // Extract API key count (with null safety)
  //   // Backend returns { keys: [...] }, keys without status are active
  //   /* eslint-disable tempots/require-async-signal-disposal -- plain array filter, not signal */
  //   const activeApiKeys =
  //     apiKeysResult.status === 'fulfilled' && apiKeysResult.value?.keys
  //       ? apiKeysResult.value.keys.filter(
  //         (k) => !k.status || k.status === 'active'
  //       ).length
  //       : 0
  //   /* eslint-enable tempots/require-async-signal-disposal */

  //   stats.set({
  //     credits,
  //     storageUsedGB,
  //     storageDaysRemaining,
  //     activeApiKeys,
  //   })

  //   // Convert jobs to activity items
  //   if (jobsResult.status === 'fulfilled') {
  //     // eslint-disable-next-line tempots/require-async-signal-disposal -- plain array, not signal
  //     const jobItems: ActivityItem[] = jobsResult.value.jobs.map(
  //       (job: JobResponse) => ({
  //         id: job.id,
  //         type: 'job' as const,
  //         title: job.actionName,
  //         subtitle: `${job.fileHashes.length} file(s)`,
  //         creditChange: job.creditsCharged ? -job.creditsCharged : undefined,
  //         timestamp: formatRelativeTime(job.createdAt),
  //         iconColor: getStatusColor(job.status),
  //       })
  //     )
  //     activity.set(jobItems)
  //   }

  //   return { stats: stats.get(), activity: activity.get() }
  // }

  // // Initial load promise
  // const initialLoad = loadDashboard()

  // // Re-load when URL projectId changes
  // projectIdSignal.on(() => loadDashboard())

  // // Re-load when workspace finishes loading (organizations become available)
  // organizations.on(() => loadDashboard())

  const storage = Workspace.projectId.mapAsync(async (id) => {
    if (id == null) return null
    const storageService = createProjectStorage(id)
    const status = await storageService.getStatus()
    return status
  }, null)
  const apiKeys = Workspace.projectId
    .mapAsync(async (id) => (id == null ? null : apiKeysService.list(id)), null)
    .map((apiKeys) => {
      return apiKeys?.keys.filter((k) => !k.status || k.status === 'active')
    })
    .map((keys) => {
      return (keys ?? []).map((k) => {
        return {
          id: k.id,
          name: k.name,
          status: k.status,
        }
      })
    })

  const activity = Workspace.projectId.mapAsync(async (id) => {
    if (id == null) return []
    const jobsService = createProjectJobs(id)
    const { jobs } = await jobsService.list({ pageSize: 5 })
    // eslint-disable-next-line tempots/require-async-signal-disposal
    return jobs.map((j: JobResponse): ActivityItem => {
      return {
        id: j.id,
        type: 'job' as const,
        title: j.actionName,
        subtitle: `${j.fileHashes.length} file(s)`,
        creditChange: j.creditsCharged ? -BigInt(j.creditsCharged) : undefined,
        timestamp: formatRelativeTime(j.createdAt),
        iconColor: getStatusColor(j.status),
      }
    })
  }, [])

  return PageLayout({
    header: 'Dashboard',
    content: Fragment(
      html.div(
        attr.class(
          'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6'
        ),
        // Credits card
        StatCard({
          title: 'Credits',
          value: Workspace.formattedCredits,
          icon: 'mdi:currency-usd',
          valueSuffix: 'cr',
          color: 'green',
          link: Workspace.organizationId.map((orgId) =>
            orgId
              ? {
                  url: `${Workspace.getOrgPath(orgId)}/billing`,
                  text: 'Add Credits',
                }
              : null
          ),
        }),
        // Storage card
        Ensure(storage, (storage) => {
          const storageSplit =
            storage.$.storage_used_bytes.map(formatBytesSplit)
          return StatCard({
            title: 'Storage',
            value: storageSplit.map((s) => s[0]),
            valueSuffix: storageSplit.map((s) => s[1]),
            subtitle: storage.map((s) =>
              s?.projected_days_remaining != null
                ? `~${s?.projected_days_remaining} days remaining`
                : undefined
            ),
            icon: 'mdi:database',
            color: 'blue',
            link: Workspace.projectId.map((pid) =>
              pid != null
                ? {
                    url: Workspace.projectUrl('/files', pid)!,
                    text: 'View Details',
                  }
                : null
            ),
          })
        }),
        // API Keys card
        StatCard({
          title: 'API Keys',

          // eslint-disable-next-line tempots/no-renderable-signal-map
          value: apiKeys.map((keys) => String(keys.length)),
          valueSuffix: 'active',
          icon: 'mdi:key',
          color: 'purple',
          link: Workspace.projectId.map((pid) =>
            pid != null
              ? {
                  url: Workspace.projectUrl('/api-keys', pid)!,
                  text: 'Manage',
                }
              : null
          ),
        })
      ),
      // Recent Activity
      html.div(
        attr.class('bg-white rounded-xl border border-gray-200'),
        html.div(
          attr.class('px-4 sm:px-6 py-4 border-b border-gray-200'),
          html.h2(
            attr.class('text-base font-semibold text-gray-900'),
            'Recent Activity'
          )
        ),
        html.div(
          attr.class('divide-y divide-gray-100'),
          // Activity items
          NotEmpty(
            activity,
            (activity) =>
              Fragment(
                html.div(
                  attr.class('px-4 sm:px-6 py-3 bg-gray-50'),
                  html.p(
                    attr.class(
                      'text-xs font-medium text-gray-500 uppercase tracking-wider'
                    ),
                    'Recent'
                  )
                ),
                ForEach(activity, (item) => ActivityItemComponent(item))
              ),
            () =>
              html.div(
                attr.class('px-4 sm:px-6 py-8 text-center'),
                Icon(
                  { icon: 'mdi:history' },
                  attr.class('w-12 h-12 text-gray-300 mx-auto mb-3')
                ),
                html.p(attr.class('text-gray-500'), 'No recent activity')
              )
          )
        ),
        html.div(
          attr.class('px-4 sm:px-6 py-4 border-t border-gray-200'),
          Anchor(
            {
              href: Workspace.projectId.map(
                (pid) => Workspace.projectUrl('/jobs', pid) ?? '#'
              ),
            },
            attr.class(
              'text-sm font-medium text-brand-600 hover:text-brand-700'
            ),
            'View All Jobs →'
          )
        )
      )
    ),
  })
}

function ActivityItemComponent(item: Signal<ActivityItem>) {
  const iconMap = {
    job: 'mdi:lightning-bolt',
    upload: 'mdi:cloud-upload',
    purchase: 'mdi:currency-usd',
  }

  const colorMap = {
    purple: 'bg-purple-100 text-purple-600',
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    red: 'bg-red-100 text-red-600',
    gray: 'bg-gray-100 text-gray-600',
  }

  return html.div(
    attr.class('px-4 sm:px-6 py-4 hover:bg-gray-50'),
    html.div(
      attr.class('flex items-start sm:items-center gap-3 sm:gap-4'),
      html.div(
        attr.class(
          item.map(
            (i) =>
              `w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${colorMap[i.iconColor]}`
          )
        ),
        Icon({ icon: item.map((i) => iconMap[i.type]) }, attr.class('w-5 h-5'))
      ),
      html.div(
        attr.class('flex-1 min-w-0'),
        html.p(
          attr.class('text-sm font-medium text-gray-900 truncate'),
          item.map((i) => i.title)
        ),
        html.p(
          attr.class('text-xs text-gray-500'),
          item.map((i) => i.subtitle)
        )
      ),
      html.div(
        attr.class('text-right flex-shrink-0'),
        When(
          item.map((i) => i.creditChange !== undefined),
          () =>
            html.p(
              attr.class(
                item.map(
                  (i) =>
                    `text-sm font-medium ${i.creditChange! > 0 ? 'text-green-600' : 'text-red-600'}`
                )
              ),
              item.map((i) =>
                formatCredits(i.creditChange!, { includeSign: true })
              )
            )
        ),
        html.p(
          attr.class('text-xs text-gray-500 whitespace-nowrap'),
          item.map((i) => i.timestamp)
        )
      )
    )
  )
}

function getStatusColor(
  status: string
): 'purple' | 'blue' | 'green' | 'red' | 'gray' {
  switch (status) {
    case 'pending':
      return 'gray'
    case 'processing':
      return 'blue'
    case 'completed':
      return 'green'
    case 'failed':
      return 'red'
    default:
      return 'purple'
  }
}
